import { useFlags } from 'launchdarkly-react-client-sdk'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getLoggedInMemberClientId, getLoggedInMemberGroupId, getOperatingHours } from 'redux/selectors/members'
import { getGroup, getGroupOperatingHoursConfigs } from 'redux/selectors/services'
import { fetchGroupOperatingHoursConfigsIfNeeded } from 'redux/thunks/clients'
import { Group, GroupOperatingHoursConfig } from 'types/client/group'
import { OperatingHours } from 'types/member/member'
import { ServiceOperatingHoursDataV1, ServiceOperatingHoursDataV2 } from './types'

export const useOperatingHours = (serviceTypes: string | string[]) => {
  const dispatch = useDispatch()
  const groupId = useSelector(getLoggedInMemberGroupId)
  const clientId = useSelector(getLoggedInMemberClientId)
  const group: Group = useSelector(getGroup(groupId))
  const { afterHoursV2 } = useFlags() || {}

  const { operatingHoursEmergencyOverrides, isAfterHours: isGroupAfterHours } = group || {}

  const normalizedServiceTypes = useMemo(() => {
    if (!serviceTypes) return []

    return Array.isArray(serviceTypes) ? serviceTypes : [serviceTypes]
  }, [serviceTypes])

  const operatingHours: OperatingHours[] | null = useSelector(getOperatingHours)

  const operatingHoursConfigs: GroupOperatingHoursConfig[] | null = useSelector(getGroupOperatingHoursConfigs(groupId))

  useEffect(() => {
    if (afterHoursV2) {
      dispatch(fetchGroupOperatingHoursConfigsIfNeeded({ groupId, clientId }))
    }
  }, [afterHoursV2, clientId, dispatch, groupId])

  const operatingHoursData = useMemo(() => {
    if (!afterHoursV2)
      return normalizedServiceTypes.reduce<Record<string, ServiceOperatingHoursDataV1>>((acc, serviceType) => {
        return { ...acc, [serviceType]: { version: 1, operatingHours } }
      }, {})

    if (!isGroupAfterHours || !operatingHoursConfigs?.length) return {}

    return normalizedServiceTypes.reduce<Record<string, ServiceOperatingHoursDataV2>>((acc, serviceType) => {
      const configsForService = operatingHoursConfigs.filter((c) => {
        return c?.careSite?.serviceTypes?.includes(serviceType)
      })

      const isEmergencyOverride = !!operatingHoursEmergencyOverrides?.find(
        (o) =>
          o.enabled &&
          o.status === 'active' &&
          o.effectiveActive &&
          o?.careSiteIds?.some(
            (emergencyCareSiteId) =>
              !!configsForService.find((serviceConfig) => serviceConfig?.careSite?.careSiteId === emergencyCareSiteId),
          ),
      )

      return {
        ...acc,
        [serviceType]: {
          version: 2,
          configs: configsForService,
          isEmergencyOverride,
        },
      }
    }, {})
  }, [
    afterHoursV2,
    isGroupAfterHours,
    normalizedServiceTypes,
    operatingHours,
    operatingHoursConfigs,
    operatingHoursEmergencyOverrides,
  ])

  const checkServiceHasOperatingHours = useCallback(
    (serviceType?: string) => {
      const serviceOperatingHoursData = operatingHoursData?.[serviceType]
      if (!serviceType || !serviceOperatingHoursData) return { hasOperatingHours: false }

      if (serviceOperatingHoursData.version === 1)
        return { hasOperatingHours: !!serviceOperatingHoursData?.operatingHours?.length }

      const { configs, isEmergencyOverride } = serviceOperatingHoursData
      return { hasOperatingHours: !!configs?.length, isEmergencyOverride }
    },
    [operatingHoursData],
  )

  return { operatingHoursData, checkServiceHasOperatingHours }
}
